import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, X, Mic, Square, Settings2 } from 'lucide-react';

const API_URL = window.location.origin || '';
const AVATAR_SRC = '/assets/dnai/dn-ai-official.png';
const POLL_INTERVAL = 5000;
const DEFAULT_CALL_INTERVAL = 25;

const STATES = { idle: 'idle', detecting: 'detecting', speaking: 'speaking', cooldown: 'cooldown' };

const api = (path, opts = {}) => fetch(`${API_URL}/api/dnago${path}`, {
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
  ...opts,
}).then(r => r.json());

function speak(text) {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) { resolve(); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'pt-BR'; u.rate = 0.9; u.pitch = 1;
    const voices = window.speechSynthesis.getVoices();
    const ptVoice = voices.find(v => v.lang.startsWith('pt')) || voices[0];
    if (ptVoice) u.voice = ptVoice;
    u.onend = resolve; u.onerror = resolve;
    window.speechSynthesis.speak(u);
  });
}

export default function DnaGoDnAiWidget() {
  const [state, setState] = useState(STATES.idle);
  const [currentText, setCurrentText] = useState('');
  const [currentName, setCurrentName] = useState('');
  const [muted, setMuted] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [callInterval, setCallInterval] = useState(() => {
    try { return Number(localStorage.getItem('dnai_call_interval')) || DEFAULT_CALL_INTERVAL; } catch { return DEFAULT_CALL_INTERVAL; }
  });
  const [showConfig, setShowConfig] = useState(false);

  // Active loops: Map<aluno_id, { text, name, stopped }>
  const activeLoopsRef = useRef(new Map());
  const [activeLoops, setActiveLoops] = useState([]); // for UI rendering
  const speakingRef = useRef(false);
  const pollRef = useRef(null);
  const loopRef = useRef(null);

  // Sync config to localStorage
  useEffect(() => {
    try { localStorage.setItem('dnai_call_interval', String(callInterval)); } catch {}
  }, [callInterval]);

  // Unlock audio on first user gesture
  useEffect(() => {
    const unlock = () => {
      setAudioUnlocked(true);
      if ('speechSynthesis' in window) {
        const u = new SpeechSynthesisUtterance('');
        u.volume = 0;
        window.speechSynthesis.speak(u);
        window.speechSynthesis.getVoices();
      }
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('touchstart', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => { window.removeEventListener('click', unlock); window.removeEventListener('touchstart', unlock); window.removeEventListener('keydown', unlock); };
  }, []);

  const handleActivateSound = () => {
    setAudioUnlocked(true);
    setMuted(false);
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance('DN-AI ativado');
      u.lang = 'pt-BR'; u.rate = 1; u.volume = 0.5;
      window.speechSynthesis.speak(u);
    }
  };

  // Build announcement text from queue item
  const buildText = (item) => {
    const nome = item.aluno_nome || 'Aluno';
    const partes = nome.split(' ');
    const primeiro = partes[0];
    const sobrenome = partes.length > 1 ? partes[partes.length - 1] : '';
    const turma = item.turma_nome || '';
    return `Atenção. ${primeiro} ${sobrenome}, da turma ${turma}, dirigir-se à saída.`;
  };

  // Register a new student for looping calls
  const startLoop = useCallback(async (item) => {
    const id = item.aluno_id;
    if (activeLoopsRef.current.has(id)) return; // Already looping

    // Register announcement in backend
    try { await api(`/announcements?aluno_id=${id}`, { method: 'POST' }); } catch {}

    const text = buildText(item);
    const nome = (item.aluno_nome || '').split(' ')[0];
    activeLoopsRef.current.set(id, { text, name: nome, stopped: false, item });
    setActiveLoops([...activeLoopsRef.current.entries()].map(([k, v]) => ({ id: k, ...v })));
  }, []);

  // Stop loop for a specific student
  const stopLoop = useCallback((alunoId) => {
    const entry = activeLoopsRef.current.get(alunoId);
    if (entry) entry.stopped = true;
    activeLoopsRef.current.delete(alunoId);
    setActiveLoops([...activeLoopsRef.current.entries()].map(([k, v]) => ({ id: k, ...v })));
    if (activeLoopsRef.current.size === 0) {
      setState(STATES.idle);
      setCurrentText('');
      setCurrentName('');
    }
  }, []);

  // Poll queue for new eligible students
  const pollQueue = useCallback(async () => {
    try {
      const items = await api('/announcements/pending');
      if (!Array.isArray(items)) return;

      // Check for removed/delivered students and stop their loops
      const currentIds = new Set(items.map(i => i.aluno_id));
      for (const [id] of activeLoopsRef.current) {
        if (!currentIds.has(id)) stopLoop(id);
      }

      // Also check full queue for status changes
      try {
        const fullQueue = await api('/fila');
        if (Array.isArray(fullQueue)) {
          for (const [id] of activeLoopsRef.current) {
            const qItem = fullQueue.find(i => i.aluno_id === id);
            if (!qItem || qItem.status === 'retirado' || qItem.status === 'delivered' || qItem.status === 'entregue') {
              stopLoop(id);
            }
          }
        }
      } catch {}

      // Start loops for new eligible items
      for (const item of items) {
        if (!activeLoopsRef.current.has(item.aluno_id)) {
          startLoop(item);
        }
      }
    } catch {}
  }, [startLoop, stopLoop]);

  // Main speech loop — cycles through active students
  useEffect(() => {
    const runLoop = async () => {
      if (speakingRef.current || !audioUnlocked || muted) return;

      const entries = [...activeLoopsRef.current.entries()].filter(([, v]) => !v.stopped);
      if (entries.length === 0) return;

      speakingRef.current = true;

      for (const [id, entry] of entries) {
        if (entry.stopped) continue;

        setState(STATES.speaking);
        setCurrentText(entry.text);
        setCurrentName(entry.name);

        await speak(entry.text);

        // Log replay in backend
        try { await api(`/announcements/${id}/replay`, { method: 'POST' }); } catch {}

        // Wait between students if multiple
        if (entries.length > 1) {
          setState(STATES.cooldown);
          await new Promise(r => setTimeout(r, 2000));
        }
      }

      setState(entries.length > 0 ? STATES.cooldown : STATES.idle);
      speakingRef.current = false;
    };

    // Run speech loop at configured interval
    loopRef.current = setInterval(runLoop, callInterval * 1000);
    // Also run once immediately if there are active loops
    const t = setTimeout(runLoop, 1000);

    return () => { clearInterval(loopRef.current); clearTimeout(t); };
  }, [callInterval, audioUnlocked, muted]);

  // Poll for new queue items
  useEffect(() => {
    pollRef.current = setInterval(pollQueue, POLL_INTERVAL);
    pollQueue(); // immediate first poll
    return () => clearInterval(pollRef.current);
  }, [pollQueue]);

  // ─── Minimized ──────────────────────────
  if (minimized) {
    return (
      <motion.button onClick={() => setMinimized(false)} data-testid="dnai-widget-mini"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-2xl border-2 border-purple-300 overflow-hidden hover:scale-110 transition-transform"
        initial={{ scale: 0 }} animate={{ scale: 1 }}>
        <img src={AVATAR_SRC} alt="DN-AI" className="w-full h-full object-cover" />
        {activeLoops.length > 0 && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center border-2 border-white">
            {activeLoops.length}
          </div>
        )}
        {state === STATES.speaking && <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-50" />}
      </motion.button>
    );
  }

  // ─── Full Widget ──────────────────────────
  return (
    <motion.div data-testid="dnai-widget"
      className="fixed bottom-6 right-6 z-50 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
      initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', damping: 20 }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-purple-900 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-purple-300 flex-shrink-0">
            <img src={AVATAR_SRC} alt="DN-AI" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-white text-xs font-bold">DN-AI</p>
            <p className="text-purple-300 text-[9px]">Loop {callInterval}s</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setShowConfig(!showConfig)} className="p-1 rounded hover:bg-white/10 transition-colors">
            <Settings2 className="w-3.5 h-3.5 text-white/50" />
          </button>
          <button onClick={() => setMuted(!muted)} className="p-1 rounded hover:bg-white/10 transition-colors">
            {muted ? <VolumeX className="w-3.5 h-3.5 text-red-300" /> : <Volume2 className="w-3.5 h-3.5 text-white/70" />}
          </button>
          <button onClick={() => setMinimized(true)} className="p-1 rounded hover:bg-white/10 transition-colors">
            <X className="w-3.5 h-3.5 text-white/50" />
          </button>
        </div>
      </div>

      {/* Config panel */}
      {showConfig && (
        <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 flex items-center gap-2">
          <span className="text-[10px] text-purple-600 whitespace-nowrap">Intervalo:</span>
          <input type="range" min={10} max={60} step={5} value={callInterval}
            onChange={e => setCallInterval(Number(e.target.value))}
            className="flex-1 h-1 accent-purple-600" />
          <span className="text-[10px] font-bold text-purple-700 w-8 text-right">{callInterval}s</span>
        </div>
      )}

      {/* Body */}
      <div className="p-4 text-center">
        {!audioUnlocked && (
          <button onClick={handleActivateSound} data-testid="dnai-activate-sound"
            className="w-full mb-3 py-2 px-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium flex items-center justify-center gap-2 hover:bg-amber-100 transition-colors">
            <Volume2 className="w-4 h-4" /> Toque para ativar o som
          </button>
        )}

        {/* Avatar */}
        <div className="relative mx-auto w-20 h-20 mb-3">
          <img src={AVATAR_SRC} alt="DN-AI" className={`w-20 h-20 rounded-full object-cover border-[3px] transition-all ${
            state === STATES.speaking ? 'border-green-400 shadow-lg shadow-green-200' :
            state === STATES.cooldown ? 'border-blue-400' :
            activeLoops.length > 0 ? 'border-yellow-400' : 'border-purple-200'
          }`} />
          {state === STATES.speaking && (
            <>
              <motion.div className="absolute inset-0 rounded-full border-2 border-green-400"
                animate={{ scale: [1, 1.3, 1], opacity: [0.8, 0, 0.8] }}
                transition={{ repeat: Infinity, duration: 1.5 }} />
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                {[0, 1, 2, 3, 4].map(i => (
                  <motion.div key={i} className="w-1 bg-green-500 rounded-full"
                    animate={{ height: [4, 12, 4] }}
                    transition={{ repeat: Infinity, duration: 0.5, delay: i * 0.1 }} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Status */}
        <div className="mb-2">
          {state === STATES.speaking ? (
            <div>
              <p className="text-xs text-green-600 font-bold flex items-center justify-center gap-1"><Mic className="w-3 h-3" /> Anunciando</p>
              <p className="text-sm font-bold text-gray-800 mt-1">{currentName}</p>
            </div>
          ) : activeLoops.length > 0 ? (
            <p className="text-xs text-yellow-600 font-medium">{activeLoops.length} aluno(s) em chamada</p>
          ) : (
            <p className="text-xs text-gray-400">Monitorando fila...</p>
          )}
        </div>

        {/* Active loops list */}
        {activeLoops.length > 0 && (
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {activeLoops.map(loop => (
              <div key={loop.id} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-yellow-50 border border-yellow-100 text-xs">
                <span className="font-medium text-gray-700 truncate flex-1 text-left">{loop.name}</span>
                <button onClick={() => stopLoop(loop.id)} title="Parar chamada"
                  className="p-1 rounded hover:bg-red-100 transition-colors flex-shrink-0 ml-1">
                  <Square className="w-3 h-3 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Status dot */}
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <div className={`w-2 h-2 rounded-full ${
            state === STATES.speaking ? 'bg-green-500 animate-pulse' :
            activeLoops.length > 0 ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'
          }`} />
          <span className="text-[10px] text-gray-400 uppercase tracking-wider">
            {state === STATES.speaking ? 'Falando' : activeLoops.length > 0 ? `Loop ${callInterval}s` : 'Pronto'}
          </span>
          {!audioUnlocked && <span className="text-[9px] text-amber-500 ml-1">som inativo</span>}
        </div>
      </div>
    </motion.div>
  );
}
